// C10 — Lógica pura del Excel de capacidades (SKU × Línea).
//
// Vive fuera del componente por una razón concreta: export e import son las dos
// mitades de un MISMO contrato. El archivo que exportamos tiene que poder
// volver a subirse sin una sola corrección. Con la lógica adentro del diálogo
// eso solo se puede verificar a ojo; acá se testea el round-trip de verdad
// (`capacidades-excel.test.ts`).
//
// Contrato de la hoja "Capacidades": una fila = una celda de la matriz, con
// TODOS los campos. El bulk del backend hace upsert de la celda completa, así
// que una fila a la que le falte una columna no actualiza: pisa.

import type { CoordinadorSkusMatriz } from 'schemas';

export const norm = (s: string) => s.trim().toLowerCase();

const SI = new Set(['si', 'sí', 's', 'true', '1', 'x', 'yes', 'y']);
const NO = new Set(['no', 'n', 'false', '0', '']);

/** Parsea un número en formato es-CO ("1.200,5") o plano. Devuelve NaN si vacío/inválido. */
export function parseNum(raw: string): number {
  const s = raw.trim();
  if (s === '') return NaN;
  // Si tiene coma decimal, los puntos son miles.
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.'));
  return Number(s.replace(/\s/g, ''));
}

export interface FilaParseada {
  idx: number;
  filaExcel: number;
  skuTexto: string;
  lineaTexto: string;
  sku_id: string | null;
  linea_id: string | null;
  capacidad: number | null;
  personas: number | null;
  golpesAplica: boolean;
  capGolpes: number | null;
  bache: number | null;
  precio: number | null;
  error: string | null;
}

/** Celda de la hoja: SheetJS con `raw:false` devuelve strings, pero el export arma números. */
export type CeldaExcel = string | number;

/** Encabezado de la hoja "Capacidades". La columna de precio depende de la capacidad. */
export function headerCapacidades(puedeVerCostos: boolean): string[] {
  return [
    'SKU (codigo)',
    'Linea (codigo o nombre)',
    'Capacidad u/h',
    'Personas por turno',
    'Golpes/min aplica (SI/NO)',
    'Capacidad golpes/min',
    'Bache minimo (opcional)',
    ...(puedeVerCostos ? ['Precio u (COP) (opcional)'] : []),
  ];
}

/**
 * Filas de datos con la configuración ACTUAL (una por celda configurada).
 *
 * Salen códigos, nunca UUIDs: son los que el import sabe resolver. Se descartan
 * las celdas cuya línea no es columna del scope (línea inactiva, por ejemplo):
 * exportarlas daría un archivo que el propio import rechaza.
 */
export function filasExportCapacidades(
  data: Pick<CoordinadorSkusMatriz, 'celdas' | 'filas' | 'lineas'>,
  puedeVerCostos: boolean,
): CeldaExcel[][] {
  const skuCodigo = new Map(data.filas.map((f) => [f.sku_id, f.codigo_cliente]));
  const lineaCodigo = new Map(data.lineas.map((l) => [l.id, l.codigo]));
  const precioPorSku = new Map(
    data.filas.filter((f) => f.precio_vigente !== null).map((f) => [f.sku_id, f.precio_vigente!]),
  );

  return (
    data.celdas
      .map((c): CeldaExcel[] | null => {
        const codSku = skuCodigo.get(c.sku_id);
        const codLinea = lineaCodigo.get(c.linea_id);
        if (!codSku || !codLinea) return null;
        const fila: CeldaExcel[] = [
          codSku,
          codLinea,
          c.capacidad_estandar_unidades_hora,
          // Nullable en BD (filas históricas). Va vacío: el dato falta de verdad,
          // no lo inventamos — y el import lo marcará en error, que es correcto.
          c.personas_estandar_turno ?? '',
          c.golpes_min_aplica ? 'SI' : 'NO',
          c.capacidad_estandar_golpes_min ?? '',
          c.bache_minimo ?? '',
        ];
        if (puedeVerCostos) fila.push(precioPorSku.get(c.sku_id) ?? '');
        return fila;
      })
      .filter((f): f is CeldaExcel[] => f !== null)
      // Orden estable y legible: por SKU, luego por línea.
      .sort(
        (a, b) =>
          String(a[0]).localeCompare(String(b[0])) || String(a[1]).localeCompare(String(b[1])),
      )
  );
}

/**
 * Parsea la hoja "Capacidades" (array de arrays, fila 0 = encabezado) y valida
 * cada fila contra el catálogo. No escribe nada: alimenta el preview.
 */
export function parsearFilasCapacidades(
  aoa: unknown[][],
  ctx: {
    skuPorCodigo: Map<string, string>;
    lineaPorTexto: Map<string, string>;
    puedeVerCostos: boolean;
  },
): FilaParseada[] {
  const out: FilaParseada[] = [];
  for (let i = 1; i < aoa.length; i++) {
    const r = aoa[i] ?? [];
    const skuTexto = String(r[0] ?? '').trim();
    const lineaTexto = String(r[1] ?? '').trim();
    const capRaw = String(r[2] ?? '').trim();
    const persRaw = String(r[3] ?? '').trim();
    const golpesRaw = String(r[4] ?? '').trim();
    const capGolpesRaw = String(r[5] ?? '').trim();
    const bacheRaw = String(r[6] ?? '').trim();
    const precioRaw = ctx.puedeVerCostos ? String(r[7] ?? '').trim() : '';
    // Fila completamente vacía → la salteamos.
    if (!skuTexto && !lineaTexto && !capRaw) continue;

    const sku_id = ctx.skuPorCodigo.get(norm(skuTexto)) ?? null;
    const linea_id = ctx.lineaPorTexto.get(norm(lineaTexto)) ?? null;
    const capacidad = parseNum(capRaw);
    const personas = parseNum(persRaw);
    const golpesNorm = norm(golpesRaw);
    const golpesAplica = SI.has(golpesNorm);
    const capGolpes = capGolpesRaw === '' ? null : parseNum(capGolpesRaw);
    const bache = bacheRaw === '' ? null : parseNum(bacheRaw);
    const precio = precioRaw === '' ? null : parseNum(precioRaw);

    let error: string | null = null;
    if (!sku_id) error = `SKU "${skuTexto}" no está en el catálogo`;
    else if (!linea_id)
      error = `Línea "${lineaTexto}" no está en su scope (use el código o nombre exacto)`;
    else if (!Number.isFinite(capacidad) || capacidad <= 0)
      error = 'Capacidad u/h inválida (debe ser > 0)';
    else if (!Number.isInteger(personas) || personas < 1 || personas > 50)
      error = 'Personas por turno inválido (entero 1–50)';
    else if (!golpesNorm || (!SI.has(golpesNorm) && !NO.has(golpesNorm)))
      error = 'Golpes/min aplica: ponga SI o NO';
    else if (golpesAplica && (capGolpes === null || !Number.isFinite(capGolpes) || capGolpes <= 0))
      error = 'Falta Capacidad golpes/min (golpes aplica = SI)';
    else if (bacheRaw !== '' && (!Number.isFinite(bache!) || bache! <= 0))
      error = 'Bache mínimo inválido (debe ser > 0)';
    else if (precioRaw !== '' && (!Number.isFinite(precio!) || precio! <= 0))
      error = 'Precio u (COP) inválido (debe ser > 0)';

    out.push({
      idx: out.length,
      filaExcel: i + 1,
      skuTexto,
      lineaTexto,
      sku_id,
      linea_id,
      capacidad: Number.isFinite(capacidad) ? capacidad : null,
      personas: Number.isInteger(personas) ? personas : null,
      golpesAplica,
      capGolpes,
      bache,
      precio: precio !== null && Number.isFinite(precio) ? precio : null,
      error,
    });
  }

  // Consistencia de precio por SKU: si el mismo SKU trae precios distintos en
  // filas distintas, esas filas quedan en error (no adivinamos cuál vale).
  if (ctx.puedeVerCostos) {
    const precioPorSku = new Map<string, number>();
    const inconsistentes = new Set<string>();
    for (const f of out) {
      if (!f.sku_id || f.precio === null) continue;
      const prev = precioPorSku.get(f.sku_id);
      if (prev === undefined) precioPorSku.set(f.sku_id, f.precio);
      else if (prev !== f.precio) inconsistentes.add(f.sku_id);
    }
    for (const f of out) {
      if (f.sku_id && inconsistentes.has(f.sku_id) && !f.error)
        f.error = 'Precio inconsistente: el SKU trae precios distintos en filas distintas';
    }
  }

  return out;
}
